'use client';

import { c as _c } from "react/compiler-runtime";
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ImageOff, Loader2, Plus } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { getS3ObjectURL } from '../../utils/s3';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
type GroupsOutput = RouterOutputs['factiiis']['mediaMatchGroups'];
type MediaMatchGroup = GroupsOutput['groups'][number];
type MediaMatchItem = MediaMatchGroup['items'][number];

// Media-match browse view: posts bucketed by visually-similar image, paginated
// server-side. Text search lives in the post filters, not here.
const MediaMatchGroups = t0 => {
  const $ = _c(24);
  const {
    factiiiIds
  } = t0;
  let t1;
  let t2;
  if ($[0] !== factiiiIds) {
    t1 = ["mediaMatchGroups", ...factiiiIds];
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return unwrapFromResult(client.factiiis.mediaMatchGroups.query({
        factiiiIds,
        cursor: pageParam as number
      }));
    };
    $[0] = factiiiIds;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = factiiiIds.length > 0;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: 0,
      getNextPageParam: _temp,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t4);
  if (isLoading) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex items-center justify-center gap-2 p-10 text-sm text-textSecondary"><Loader2 size={16} className="animate-spin" />Finding matching images…</div>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  if (isError || !data) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="p-10 text-center text-sm text-destructive">Couldn't load images. Try again.</p>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  const t5 = data.pages;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== data.pages) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const groups = t5.flatMap(_temp2);
      if (groups.length === 0) {
        let t10;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <div className="flex flex-col items-center gap-2 p-12 text-center text-textSecondary"><ImageOff size={28} strokeWidth={1.5} /><p className="text-sm">No images shared here yet.</p></div>;
          $[14] = t10;
        } else {
          t10 = $[14];
        }
        t9 = t10;
        break bb0;
      }
      t6 = "space-y-4";
      t7 = "factiii-media-match-groups";
      t8 = groups.map(_temp3);
    }
    $[9] = data.pages;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[15] !== fetchNextPage || $[16] !== hasNextPage || $[17] !== isFetchingNextPage) {
    t10 = hasNextPage && <div className="flex justify-center"><Button variant="SECONDARY" text={isFetchingNextPage ? "Loading\u2026" : "Load more groups"} loading={isFetchingNextPage} onClick={() => void fetchNextPage()} /></div>;
    $[15] = fetchNextPage;
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t11 = <div className={t6} data-id={t7}>{t8}{t10}</div>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};

// One group: shows a few thumbs, then a "View more" tile that expands in place.
const MediaMatchGroupSection = t0 => {
  const $ = _c(28);
  const {
    group
  } = t0;
  const [expanded, setExpanded] = useState(false);
  let t1;
  let t2;
  if ($[0] !== group.clusterId) {
    t1 = ["mediaMatchGroupItems", group.clusterId];
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return unwrapFromResult(client.factiiis.mediaMatchGroupItems.query({
        clusterId: group.clusterId,
        cursor: pageParam as number
      }));
    };
    $[0] = group.clusterId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== expanded || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: 0,
      getNextPageParam: _temp4,
      enabled: expanded
    };
    $[3] = expanded;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useInfiniteQuery(t3);
  let t4;
  if ($[7] !== data?.pages) {
    t4 = data?.pages.flatMap(_temp5);
    $[7] = data?.pages;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const loaded = t4;
  const items = expanded && loaded ? loaded : group.items;
  const showMore = expanded ? hasNextPage : group.hasMore;
  const hasDuplicates = group.items.length > 1 || group.hasMore;
  const t5 = `cluster ${group.clusterId}`;
  const t6 = hasDuplicates ? "Duplicate images" : "Unique image";
  let t7;
  if ($[9] !== t6) {
    t7 = <span className="text-sm font-emphasis text-textSecondary">{t6}</span>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <header className="mb-3 flex items-center gap-2" title={t5}>{t7}</header>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== items) {
    t9 = items.map(_temp6);
    $[14] = items;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== expanded || $[17] !== fetchNextPage || $[18] !== hasNextPage || $[19] !== isFetching || $[20] !== showMore) {
    t10 = showMore && <button onClick={() => expanded ? void fetchNextPage() : setExpanded(true)} disabled={isFetching && !hasNextPage} className="group flex aspect-square flex-col items-center justify-center gap-1 rounded-xl bg-background-light text-textSecondary ring-1 ring-inset ring-ds-border-subtle transition-colors hover:bg-background-secondary hover:text-textPrimary">{isFetching ? <Loader2 size={20} className="animate-spin" /> : <><span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-secondary transition-colors group-hover:bg-primary/10 group-hover:text-primary"><Plus size={18} /></span><span className="text-xs font-medium">View more</span></>}</button>;
    $[16] = expanded;
    $[17] = fetchNextPage;
    $[18] = hasNextPage;
    $[19] = isFetching;
    $[20] = showMore;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t8) {
    t12 = <section className="rounded-2xl border border-ds-border-subtle bg-background-primary p-4">{t8}{t11}</section>;
    $[25] = t11;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
};
const ImageThumb = t0 => {
  const $ = _c(19);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.image) {
    t1 = item.image ? getS3ObjectURL(item.image.key) : null;
    $[0] = item.image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const src = t1;
  const t2 = `/post/${item.postUuid}`;
  let t3;
  if ($[2] !== item.user.username || $[3] !== src) {
    t3 = src ? <img src={src} alt={`Image by @${item.user.username}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center text-textSecondary"><ImageOff size={18} strokeWidth={1.5} /></div>;
    $[2] = item.user.username;
    $[3] = src;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== item.user.avatarKey || $[6] !== item.user.isBot || $[7] !== item.user.robohash || $[8] !== item.user.userId) {
    t4 = <Avatar avatarKey={item.user.avatarKey} robohash={item.user.robohash} isBot={item.user.isBot} userId={item.user.userId} size={20} />;
    $[5] = item.user.avatarKey;
    $[6] = item.user.isBot;
    $[7] = item.user.robohash;
    $[8] = item.user.userId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== item.user.username) {
    t5 = <span className="truncate text-xs font-medium text-white drop-shadow">@{item.user.username}</span>;
    $[10] = item.user.username;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-2 pb-2 pt-6 opacity-0 transition-opacity duration-200 group-hover:opacity-100">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t6) {
    t7 = <Link href={t2} className="group relative block aspect-square overflow-hidden rounded-xl bg-background-light ring-1 ring-inset ring-ds-border-subtle">{t3}{t6}</Link>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
export default MediaMatchGroups;
function _temp(lastPage) {
  return lastPage.nextCursor ?? undefined;
}
function _temp2(page) {
  return page.groups;
}
function _temp3(group) {
  return <MediaMatchGroupSection key={group.clusterId} group={group} />;
}
function _temp4(lastPage) {
  return lastPage.nextCursor ?? undefined;
}
function _temp5(page) {
  return page.items;
}
function _temp6(item) {
  return <ImageThumb key={item.postFactiiiId} item={item} />;
}